/**
 * queue/seed.ts
 * Initial in-memory state for the demo restaurant ("Spice Route · Indiranagar"),
 * ported from the prototype's constructor + _seed(). 11 tables, 4 seated parties,
 * 1 CALLED party with a held table (feeds the now-serving board), 6 waiting.
 *
 * In production this state comes from /v1 (entries from sk-queue, resources from
 * SK Calendar by reference). Here it's seeded locally and resets on reload.
 */

import type { QueueState, Resource, Entry, EntryEvent, Channel } from './types';

const BASE_RESOURCES: Omit<Resource, 'status' | 'occupant' | 'heldFor'>[] = [
  { id: 'T1', label: 'T1', cap: 2, zone: 'Main', shared: false },
  { id: 'T2', label: 'T2', cap: 2, zone: 'Main', shared: false },
  { id: 'T3', label: 'T3', cap: 4, zone: 'Main', shared: false },
  { id: 'T4', label: 'T4', cap: 4, zone: 'Main', shared: false },
  { id: 'T5', label: 'T5', cap: 4, zone: 'Main', shared: false },
  { id: 'T6', label: 'T6', cap: 6, zone: 'Main', shared: false },
  { id: 'T7', label: 'T7', cap: 6, zone: 'Main', shared: false },
  { id: 'T8', label: 'T8', cap: 8, zone: 'Main', shared: false },
  { id: 'P1', label: 'P1', cap: 2, zone: 'Patio', shared: false },
  { id: 'P2', label: 'P2', cap: 4, zone: 'Patio', shared: false },
  { id: 'B1', label: 'B1', cap: 8, zone: 'Bar', shared: true },
];

export function createInitialState(T0: number): QueueState {
  const mkEv = (type: any, actor: any, agoMin: number, reason?: string): EntryEvent => ({
    type, actor, at: T0 - agoMin * 60000, reason,
  });

  const resources: Resource[] = BASE_RESOURCES.map(r => ({
    ...r, status: 'free', occupant: null, heldFor: null,
  }));
  const setRes = (id: string, patch: Partial<Resource>) => {
    const r = resources.find(x => x.id === id);
    if (r) Object.assign(r, patch);
  };

  const entries: Entry[] = [];

  // ── Seated parties occupying tables. Tokens 40–43 (prototype's seq quirk:
  //    the first seated party is token 40, then 41, 42, 43). ───────────────
  const seated = [
    { id: 'e_verma',  name: 'Verma',  phone: '9845011223', party: 4, channel: 'WEB',   res: 'T3', agoJoin: 64, agoSeat: 48, token: '40' },
    { id: 'e_reddy',  name: 'Reddy',  phone: '9845022334', party: 5, channel: 'QR',    res: 'T6', agoJoin: 58, agoSeat: 41, token: '41' },
    { id: 'e_kapoor', name: 'Kapoor', phone: '9845033445', party: 7, channel: 'STAFF', res: 'T8', agoJoin: 52, agoSeat: 39, token: '42' },
    { id: 'e_bar',    name: 'Das',    phone: '9845044556', party: 3, channel: 'STAFF', res: 'B1', agoJoin: 46, agoSeat: 33, token: '43' },
  ];
  seated.forEach(s => {
    entries.push({
      id: s.id, token: s.token, displayName: s.name, phone: s.phone, party: s.party,
      channel: s.channel as Channel, state: 'SEATED', joinAt: T0 - s.agoJoin * 60000,
      allocatedResource: s.res, consent: true, verified: true,
      events: [
        mkEv('JOINED', 'STAFF', s.agoJoin),
        mkEv('VERIFIED', 'SYSTEM', s.agoJoin - 1),
        mkEv('WAITING', 'SYSTEM', s.agoJoin - 1),
        mkEv('ALLOCATED', 'SYSTEM', s.agoSeat + 1),
        mkEv('SEATED', 'STAFF', s.agoSeat),
      ],
    });
    setRes(s.res, { status: 'occupied', occupant: s.id });
  });

  // ── A CALLED party with a held table (feeds the now-serving board). ──────
  const called: Entry = {
    id: 'e_joshi', token: '46', displayName: 'Anil Joshi', phone: '9811765432', party: 4,
    channel: 'QR', state: 'CALLED', joinAt: T0 - 27 * 60000, allocatedResource: 'T5',
    consent: true, verified: true,
    events: [
      mkEv('JOINED', 'GUEST', 27), mkEv('VERIFIED', 'SYSTEM', 26),
      mkEv('WAITING', 'SYSTEM', 26), mkEv('CALLED', 'SYSTEM', 2),
    ],
  };
  entries.push(called);
  setRes('T5', { status: 'held', heldFor: called.id });

  // ── Waiting line (FIFO, earliest first). Tokens 41–45,47 per prototype. ──
  const wait = [
    { id: 'e_priya',   name: 'Priya Nair',   phone: '9900112233', party: 2, channel: 'QR',       ago: 22, token: '41' },
    { id: 'e_arjun',   name: 'Arjun Mehta',  phone: '9900223344', party: 4, channel: 'VOICE',    ago: 18, token: '42' },
    { id: 'e_fatima',  name: 'Fatima Khan',  phone: '9900334455', party: 6, channel: 'STAFF',    ago: 15, token: '43' },
    { id: 'e_rohan',   name: 'Rohan Gupta',  phone: '9900445566', party: 2, channel: 'WHATSAPP', ago: 11, token: '44' },
    { id: 'e_lakshmi', name: 'Lakshmi Iyer', phone: '9900556677', party: 4, channel: 'QR',       ago: 7,  token: '45' },
    { id: 'e_sandeep', name: 'Sandeep Rao',  phone: '9900667788', party: 3, channel: 'WEB',      ago: 3,  token: '47' },
  ];
  wait.forEach(w => {
    entries.push({
      id: w.id, token: w.token, displayName: w.name, phone: w.phone, party: w.party,
      channel: w.channel as Channel, state: 'WAITING', joinAt: T0 - w.ago * 60000,
      allocatedResource: null, consent: true, verified: true,
      events: [
        mkEv('JOINED', w.channel === 'STAFF' ? 'STAFF' : 'GUEST', w.ago),
        mkEv('VERIFIED', 'SYSTEM', w.ago - 1),
        mkEv('WAITING', 'SYSTEM', w.ago - 1),
      ],
    });
  });

  return {
    queueName: 'Spice Route · Indiranagar',
    now: T0,
    cfg1: { matchType: 'FITTED', maxParallel: 8, ackGraceSec: 60, agingCap: 0, allowOversize: false, oversizeThreshold: 0 },
    cfg2: {
      self_join: true, shared_seating: false, accepting: true, self_removal: true,
      ch_QR: true, ch_VOICE: true, ch_STAFF: true, ch_WEB: true, ch_WHATSAPP: true,
      deposit: false, depositAmt: 200, no_show: true, noShowAmt: 300, maxParty: 8,
      // Lifecycle windows — moved to Tier-2 (org-admin tunable).
      holdMin: 5, graceMin: 5, maxWaitMin: 90,
    },
    resources,
    entries,
    guest: { step: 'join', party: 2, name: '', phone: '', consent: false, otp: '', entryId: null },
    voice: {
      maxParallel: 8, asa: '0:28', abandoned: 4, sl: 82, escalatedToday: 6,
      calls: [
        { token: 'V20', name: 'Ramesh K.',  phone: '98450 11020', intent: 'Table for 4 · 8:30 PM',  lang: 'Hindi',   startAt: T0 - 95000,  escalating: false, feeds: true },
        { token: 'V21', name: 'Anjali S.',  phone: '98450 22021', intent: 'Reschedule booking',      lang: 'English', startAt: T0 - 140000, escalating: false, feeds: false },
        { token: 'V22', name: 'Murugan P.', phone: '98450 33022', intent: 'Veg menu · Jain options', lang: 'Tamil',   startAt: T0 - 40000,  escalating: false, feeds: false },
        { token: 'V23', name: 'Sneha R.',   phone: '98450 44023', intent: 'Cancel reservation',      lang: 'Hindi',   startAt: T0 - 22000,  escalating: false, feeds: false },
        { token: 'V24', name: 'Imran A.',   phone: '98450 55024', intent: 'Refund dispute',          lang: 'English', startAt: T0 - 205000, escalating: true,  feeds: false },
        { token: 'V25', name: 'Deepa N.',   phone: '98450 66025', intent: 'Large party · 12 guests', lang: 'English', startAt: T0 - 60000,  escalating: true,  feeds: true },
        { token: 'V26', name: 'Karthik V.', phone: '98450 77026', intent: 'Opening hours',           lang: 'Tamil',   startAt: T0 - 15000,  escalating: false, feeds: false },
        { token: 'V27', name: 'Pooja M.',   phone: '98450 88027', intent: 'Directions to outlet',    lang: 'Hindi',   startAt: T0 - 30000,  escalating: false, feeds: false },
      ],
      hold: [
        { token: 'V28', name: 'Arvind T.', phone: '98450 99028', startAt: T0 - 48000, deflect: false },
        { token: 'V29', name: 'Fatima Z.', phone: '98451 00029', startAt: T0 - 95000, deflect: true },
        { token: 'V30', name: 'Suresh B.', phone: '98451 11030', startAt: T0 - 26000, deflect: false },
      ],
      agents: [
        { name: 'Meera Joshi',  status: 'on-call',   forName: 'Imran A.', reason: 'Refund dispute', startAt: T0 - 150000 },
        { name: 'Vikram Sethi', status: 'wrap-up',   forName: null, reason: null, startAt: null },
        { name: 'Lata Menon',   status: 'available', forName: null, reason: null, startAt: null },
      ],
    },
    chat: {
      perAgent: 1, longest: '1:30', abandoned: 2,
      bots: [
        { token: 'C40', name: 'Nikhil R.', channel: 'WhatsApp',  intent: 'Order status',        startAt: T0 - 65000,  escalating: false, feeds: false },
        { token: 'C41', name: 'Priya D.',  channel: 'Web',       intent: 'Book a table · 6 ppl', startAt: T0 - 30000,  escalating: true,  feeds: true },
        { token: 'C42', name: 'Sana K.',   channel: 'Instagram', intent: 'Veg menu query',       startAt: T0 - 18000,  escalating: false, feeds: false },
        { token: 'C43', name: 'Ravi T.',   channel: 'WhatsApp',  intent: 'Refund request',       startAt: T0 - 120000, escalating: true,  feeds: false },
      ],
      waiting: [
        { token: 'C44', name: 'Aisha M.',  channel: 'WhatsApp', intent: 'Complaint · cold food', startAt: T0 - 90000 },
        { token: 'C45', name: 'Gaurav S.', channel: 'Web',      intent: 'Bulk catering quote',   startAt: T0 - 50000 },
      ],
      agents: [
        { name: 'Neha Kulkarni', status: 'busy', forName: 'Rahul V.', intent: 'Allergy info',  startAt: T0 - 200000 },
        { name: 'Arjun Pillai',  status: 'busy', forName: 'Divya N.', intent: 'Late delivery', startAt: T0 - 160000 },
        { name: 'Sara Thomas',   status: 'away', forName: null, intent: null, startAt: null },
      ],
    },
  };
}
